import { useEffect, useMemo, useRef, useState } from 'react'
import type { Map as MapboxMap, Marker as MapboxMarker } from 'mapbox-gl'
import { MapPin, RefreshCw } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { FilterBar } from '@/shared/ui/filters'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { hasMapbox, loadMapbox, styleUrl, KENYA_BOUNDS } from '@/shared/map/mapbox'
import type { MapStyleChoice } from '@/shared/map/mapbox'
import { fmtNumber } from '@/shared/lib/format'
import { listCoverageEvents, listLiveCoverage } from './api'
import { CoverageEventDrawer } from './EventDrawer'
import {
  EVENT_STATUS_ORDER, classificationLabel, eventStatusLabel, eventTypeLabel,
  isSensitive, placeOf,
} from './vocab'
import type { CoverageEventResponse } from '@/shared/api/types'

/**
 * Where the cameras are.
 *
 * ## Two different coordinates, and the map says which one it drew
 *
 * `event.latitude/longitude` is where somebody **planned** it, typed in days
 * earlier. `feed.capturedLatitude/-Longitude` is where the **device** was when it
 * went on air. Those are different claims and only the second is evidence, so
 * this map prefers the camera's position, falls back to the planned one, and
 * **labels which it used** — a filled pin for a device fix, a hollow one for a
 * plan. Drawing them identically would let "we think it is here" pass for "the
 * camera was here".
 *
 * `locationAccuracyM` is drawn as a real circle in metres. A 78 m GPS fix and a
 * 2 km wifi estimate are not the same evidence, and a map that renders both as
 * one confident dot is the thing the backend stored the accuracy to prevent.
 *
 * ## What this map does and does not answer
 *
 * It answers **where**, and — from `GET /coverage/live` — which of those has a
 * camera on air. It does NOT answer "where are we not filming": that is a
 * question about units with no events, and a map of events cannot show an absence
 * of events. `/coverage/analytics` answers it with the server doing the counting.
 *
 * ## The number that has to be on this screen
 *
 * An event reaches the map only if it has coordinates from one source or the
 * other, and plenty will have neither. A map showing six markers over a diary of
 * forty **looks like a diary of six** — the reader cannot tell "we are filming in
 * six places" from "thirty-four events have no coordinates". So the count of
 * unplaced events is stated above the map, permanently, and they are listed
 * underneath rather than dropped.
 *
 * This is the same defect as a coverage table that omits units with no events:
 * absence rendered as a fact rather than as a gap.
 */

/** Where a marker's coordinates came from. Rendered differently, deliberately. */
type Origin = 'device' | 'planned'

type Placed = CoverageEventResponse & {
  lat: number
  lon: number
  origin: Origin
  /** Metres, when the device reported one. Drawn as a circle. */
  accuracyM: number | null
  /** When the DEVICE took the fix — not when the event started. */
  locatedAt: string | null
  /** The camera the fix came from, for the popup. */
  fixFrom: string | null
}

const num = (v: string | number | null | undefined): number | null => {
  if (v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * The best position for one event, and where it came from.
 *
 * **The camera wins.** Backend prompt 47 §B.5 is explicit: plot the camera's
 * position where it exists and fall back to the event's planned one where it does
 * not. When several cameras have a fix, the most recently located one is used —
 * a rally's newest fix is the one describing where the rally is now.
 */
function positionOf(e: CoverageEventResponse): Placed | null {
  const located = (e.feeds ?? [])
    .map((f) => ({ f, lat: num(f.capturedLatitude), lon: num(f.capturedLongitude) }))
    .filter((c) => c.lat != null && c.lon != null)
    .sort((a, b) => (b.f.locatedAt ?? '').localeCompare(a.f.locatedAt ?? ''))

  const best = located[0]
  if (best) {
    return {
      ...e,
      lat: best.lat as number,
      lon: best.lon as number,
      origin: 'device',
      accuracyM: best.f.locationAccuracyM,
      locatedAt: best.f.locatedAt,
      fixFrom: best.f.title,
    }
  }
  const lat = num(e.latitude)
  const lon = num(e.longitude)
  if (lat == null || lon == null) return null
  return { ...e, lat, lon, origin: 'planned', accuracyM: null, locatedAt: null, fixFrom: null }
}

/**
 * Marker colour by what the event is doing. Live is the only one that pulses.
 *
 * **Live still comes from `GET /coverage/live`, not from `event.feeds`.** The
 * list endpoint does now send feeds — backend prompt 46 §C, in two extra queries
 * for the whole page rather than two per row — and the popup reads them for
 * camera counts and recording state. But "on air right now" is a fact with a
 * lifetime of seconds, and `/coverage/live` is the endpoint that answers it for
 * every event at once; deriving it from a list page fetched on a different tick
 * would make the pulse lag the truth.
 *
 * There is deliberately NO "filmed / nobody filmed" pair on this map. Guessing
 * from `statusCode === 'ENDED'` would call an ended event "filmed" when the
 * camera never started, which is the precise claim the coverage analytics screen
 * exists to make honestly — with the server counting.
 */
const ACCURACY_SRC = 'coverage-accuracy'
const ACCURACY_FILL = 'coverage-accuracy-fill'
const ACCURACY_LINE = 'coverage-accuracy-line'

function markerTone(e: CoverageEventResponse, live: boolean): { fill: string; ring: string; live: boolean } {
  if (live) return { fill: '#dc2626', ring: 'rgba(220,38,38,0.35)', live: true }
  if (e.statusCode === 'SCHEDULED') return { fill: '#d97706', ring: 'rgba(217,119,6,0.25)', live: false }
  if (e.statusCode === 'CANCELLED') return { fill: '#94a3b8', ring: 'rgba(148,163,184,0.2)', live: false }
  return { fill: '#7B3F00', ring: 'rgba(123,63,0,0.25)', live: false }
}

export function CoverageMapPage() {
  const canRead = useCan('coverage:read')
  const [status, setStatus] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const { data, error, loading } = useLoad(
    () => listCoverageEvents({ status: status || undefined }, 0, 200), [status, tick],
  )

  const events = useMemo(() => data?.content ?? [], [data])
  const placed = useMemo(
    () => events.map(positionOf).filter((p): p is Placed => p !== null),
    [events],
  )
  const fromDevice = placed.filter((p) => p.origin === 'device').length
  const unplaced = events.length - placed.length

  const liveFeeds = useLoad(() => listLiveCoverage(), [tick])
  const liveEventIds = useMemo(
    () => new Set((liveFeeds.data ?? []).map((f) => f.eventId)),
    [liveFeeds.data],
  )
  const live = placed.filter((e) => liveEventIds.has(e.id)).length

  if (!canRead) return <Forbidden what="live coverage" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Coverage Map"
        description="Where the events are, and which of them has a camera on air."
        right={
          <button type="button" className="erp-btn" aria-label="Refresh"
            onClick={() => setTick((t) => t + 1)}>
            <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          </button>
        }
      />

      <FilterBar>
        <select className="erp-input h-9" value={status} aria-label="Status"
          onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {EVENT_STATUS_ORDER.map((s) => <option key={s} value={s}>{eventStatusLabel(s)}</option>)}
        </select>
        <Legend />
      </FilterBar>

      <ErrorStrip message={error} />

      {/*
        Stated ABOVE the map, not below it and not in a tooltip. Somebody who
        glances at this screen and walks away must not carry off a count that is
        wrong by the number of events nobody geocoded.
      */}
      {events.length > 0 && (
        <Note tone={unplaced > 0 ? 'bad' : 'good'}>
          {unplaced === 0
            ? <>
              All {fmtNumber(events.length)} events in this view are on the map
              {fromDevice > 0 && <>, {fmtNumber(fromDevice)} of them at a position a camera
                actually reported</>}.
            </>
            : <>
              <strong>{fmtNumber(placed.length)} of {fmtNumber(events.length)}</strong> events are on
              this map{fromDevice > 0 && <> ({fmtNumber(fromDevice)} at a camera's own position)</>}.
              The other {fmtNumber(unplaced)} have no coordinates from either a camera or the diary
              and are listed below — they are missing from the picture, not missing from the diary.
            </>}
        </Note>
      )}

      {loading && events.length === 0 ? <LoadingBlock label="Loading the diary…" /> : (
        <>
          <Panel
            title="On the ground"
            description={live > 0
              ? `${fmtNumber(live)} of these has a camera on air right now. `
                + 'Scroll to zoom; a shaded circle is a camera fix drawn at its stated accuracy.'
              : 'Nothing is broadcasting right now. Scroll to zoom; a shaded circle is a '
                + 'camera fix drawn at its stated accuracy.'}
          >
            {hasMapbox() ? (
              <CoverageMap events={placed} liveIds={liveEventIds} onOpen={setOpenId} />
            ) : (
              <Note tone="info">
                No Mapbox token is configured for this deployment, so there is no map. The
                list below is the same data.
              </Note>
            )}
          </Panel>

          {unplaced > 0 && (
            <Panel
              title={`Not on the map (${fmtNumber(unplaced)})`}
              description="No coordinates recorded. Add them by editing the event."
            >
              <ul className="space-y-1">
                {events.filter((e) => !placed.some((p) => p.id === e.id)).map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      className="w-full rounded-md px-2 py-1.5 text-left hover:bg-muted"
                      onClick={() => setOpenId(e.id)}
                    >
                      <span className="text-erp-sm font-medium">{e.title}</span>
                      <span className="ml-2 text-erp-xs text-muted-foreground">
                        {eventTypeLabel(e.eventTypeCode)} · {placeOf(e)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      )}

      {openId && (
        <CoverageEventDrawer
          eventId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}
    </div>
  )
}

function Legend() {
  const items: [string, string][] = [
    ['#dc2626', 'On air'],
    ['#d97706', 'Scheduled'],
    ['#7B3F00', 'Finished'],
    ['#94a3b8', 'Cancelled'],
  ]
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map(([c, label]) => (
        <span key={label} className="flex items-center gap-1.5 text-erp-xs text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
          {label}
        </span>
      ))}
      {/*
        The shape carries the provenance, so the legend has to teach it. Colour
        says what the event is doing; fill says whether the position is a
        measurement or somebody's plan.
      */}
      <span className="ml-1 flex items-center gap-1.5 text-erp-xs text-muted-foreground">
        <span className="h-2.5 w-2.5 rounded-full border-2 border-white bg-muted-foreground" />
        Camera fix
      </span>
      <span className="flex items-center gap-1.5 text-erp-xs text-muted-foreground">
        <span className="h-2.5 w-2.5 rounded-full border-2 border-dashed border-muted-foreground" />
        Planned only
      </span>
    </div>
  )
}

function CoverageMap({
  events, liveIds, onOpen,
}: { events: Placed[]; liveIds: Set<string>; onOpen: (id: string) => void }) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapboxMap | null>(null)
  const markers = useRef<MapboxMarker[]>([])
  /** Latest `onOpen` without re-registering every marker's handler each render. */
  const open = useRef(onOpen)
  open.current = onOpen

  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))
  /**
   * Satellite matters here for the same reason it does when siting a polling
   * station: "the field behind the school" is findable on imagery and invisible
   * on a street map, and a camera's fix often lands on open ground.
   */
  const [basemap, setBasemap] = useState<MapStyleChoice>('theme')
  /** Bumped when a style swap wipes the layers, so the markers effect re-runs. */
  const [styleEpoch, setStyleEpoch] = useState(0)

  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])

  // Created once. Recreating on every data change would flash and throw away the
  // viewport somebody just panned to.
  useEffect(() => {
    if (!host.current) return
    let cancelled = false
    loadMapbox().then((mapboxgl) => {
      if (cancelled || !host.current) return
      const m = new mapboxgl.Map({
        container: host.current,
        style: styleUrl('theme', document.documentElement.classList.contains('dark')),
        bounds: KENYA_BOUNDS,
        fitBoundsOptions: { padding: 24 },
        // cooperativeGestures made the wheel require ctrl, which is the right
        // default for a map embedded in a long scrolling page and the wrong one
        // here: this map IS the page, and the panel is a fixed height, so the
        // wheel has nothing else to do.
        cooperativeGestures: false,
      })
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
      m.addControl(new mapboxgl.FullscreenControl(), 'top-right')
      m.addControl(new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left')
      m.on('load', () => { if (!cancelled) setState('ready') })
      m.on('error', () => { if (!cancelled) setState('failed') })
      map.current = m
    }).catch(() => { if (!cancelled) setState('failed') })

    return () => {
      cancelled = true
      markers.current.forEach((mk) => mk.remove())
      markers.current = []
      map.current?.remove()
      map.current = null
    }
  }, [])

  // Restyling drops every layer the map owns, so the accuracy circles are rebuilt
  // once the new style has loaded rather than left behind as ghosts.
  useEffect(() => {
    const m = map.current
    if (!m || state !== 'ready') return
    m.setStyle(styleUrl(basemap, dark))
    m.once('styledata', () => setStyleEpoch((n) => n + 1))
  }, [dark, basemap, state])

  // Markers are rebuilt wholesale on every change. There are at most 200 and the
  // alternative — diffing by id — is a cache to keep correct for no measurable
  // gain at this size.
  useEffect(() => {
    const m = map.current
    if (!m || state !== 'ready') return

    markers.current.forEach((mk) => mk.remove())
    markers.current = []

    /**
     * Accuracy, as a circle in METRES.
     *
     * Not `circle-radius`, which is pixels and would keep one apparent size as
     * you zoom — an 80 m fix and a 2 km one would look identical, which is the
     * confusion the accuracy column exists to remove. A 64-gon in lat/long is
     * exact enough at any scale this map shows.
     */
    const ring = (lat: number, lon: number, metres: number): [number, number][] => {
      const points: [number, number][] = []
      const dLat = metres / 111_320
      const dLon = metres / (111_320 * Math.cos((lat * Math.PI) / 180))
      for (let i = 0; i <= 64; i++) {
        const t = (i / 64) * 2 * Math.PI
        points.push([lon + dLon * Math.cos(t), lat + dLat * Math.sin(t)])
      }
      return points
    }

    const circles = {
      type: 'FeatureCollection' as const,
      features: events
        .filter((e) => e.origin === 'device' && e.accuracyM != null && e.accuracyM > 0)
        .map((e) => ({
          type: 'Feature' as const,
          properties: { live: liveIds.has(e.id) ? 1 : 0 },
          geometry: { type: 'Polygon' as const, coordinates: [ring(e.lat, e.lon, e.accuracyM as number)] },
        })),
    }

    const paint = () => {
      if (!map.current) return
      const src = map.current.getSource(ACCURACY_SRC)
      if (src) {
        ;(src as { setData: (d: unknown) => void }).setData(circles)
        return
      }
      map.current.addSource(ACCURACY_SRC, { type: 'geojson', data: circles })
      map.current.addLayer({
        id: ACCURACY_FILL,
        type: 'fill',
        source: ACCURACY_SRC,
        paint: {
          'fill-color': ['case', ['==', ['get', 'live'], 1], '#dc2626', '#0ea5e9'],
          'fill-opacity': 0.1,
        },
      })
      map.current.addLayer({
        id: ACCURACY_LINE,
        type: 'line',
        source: ACCURACY_SRC,
        paint: {
          'line-color': ['case', ['==', ['get', 'live'], 1], '#dc2626', '#0ea5e9'],
          'line-width': 1,
          'line-opacity': 0.45,
        },
      })
    }
    if (m.isStyleLoaded()) paint(); else m.once('styledata', paint)

    void loadMapbox().then((mapboxgl) => {
      if (!map.current) return
      for (const e of events) {
        const tone = markerTone(e, liveIds.has(e.id))
        const el = document.createElement('button')
        el.type = 'button'
        el.setAttribute('aria-label',
          `${e.title} — ${placeOf(e)}, ${e.origin === 'device' ? 'camera position' : 'planned position'}`)
        el.style.cssText = [
          'width:16px', 'height:16px', 'border-radius:9999px', 'cursor:pointer',
          'padding:0', 'transition:transform 120ms ease',
          // A DEVICE fix is solid; a PLANNED one is hollow. The difference is the
          // difference between evidence and intent, and it has to be visible
          // without opening anything.
          e.origin === 'device' ? `background:${tone.fill}` : 'background:transparent',
          e.origin === 'device'
            ? 'border:2px solid rgba(255,255,255,0.9)'
            : `border:2.5px dashed ${tone.fill}`,
          `box-shadow:0 0 0 4px ${tone.ring}`,
        ].join(';')
        if (tone.live) el.animate(
          [{ boxShadow: `0 0 0 3px ${tone.ring}` }, { boxShadow: `0 0 0 12px transparent` }],
          { duration: 1600, iterations: Infinity },
        )
        el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.35)' })
        el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)' })
        el.addEventListener('click', (ev) => { ev.stopPropagation(); open.current(e.id) })

        const popup = new mapboxgl.Popup({ offset: 16, closeButton: false, maxWidth: '280px' })
          .setHTML(popupHtml(e, liveIds.has(e.id)))

        markers.current.push(
          new mapboxgl.Marker({ element: el })
            .setLngLat([e.lon, e.lat])
            .setPopup(popup)
            .addTo(map.current),
        )
      }
    })
  }, [events, liveIds, state, styleEpoch])

  return (
    <div className="relative">
      <div ref={host} className="h-[520px] w-full overflow-hidden rounded-md bg-muted" />
      {state === 'ready' && (
        <div className="absolute left-2 top-2 flex overflow-hidden rounded-md border border-border bg-background/90 shadow-sm">
          {(['theme', 'satellite'] as const).map((choice) => (
            <button
              key={choice}
              type="button"
              className={`px-2.5 py-1 text-erp-xs capitalize ${
                basemap === choice ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setBasemap(choice)}
            >
              {choice === 'theme' ? 'Map' : 'Satellite'}
            </button>
          ))}
        </div>
      )}
      {state === 'loading' && (
        <div className="absolute inset-0 grid place-items-center rounded-md bg-background/60">
          <LoadingBlock label="Loading the map…" />
        </div>
      )}
      {state === 'failed' && (
        <div className="absolute inset-0 grid place-items-center rounded-md bg-background/80 p-6">
          <p className="max-w-sm text-center text-erp-sm text-muted-foreground">
            <MapPin className="mx-auto mb-1 h-5 w-5" />
            The map could not load. Everything else on this screen still works — the list
            below has the same events.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * What the popup says, and why each line is on it.
 *
 * The map's job is to let somebody decide whether to open an event, so every line
 * here answers a question they would otherwise open the drawer for: is anyone
 * filming, how many cameras, how good is this position, and is this position even
 * a measurement.
 *
 * **The provenance line is not optional.** A hollow pin says "planned" at a
 * glance; this says it in words, with the accuracy when there is one. A reader
 * who cannot tell a 78 m GPS fix from a 2 km wifi estimate will treat both as the
 * truth about which ward the camera was in.
 */
function popupHtml(e: Placed, live: boolean): string {
  const feeds = e.feeds ?? []
  const onAir = feeds.filter((f) => f.statusCode === 'LIVE').length
  const viewers = feeds.reduce((n, f) => n + (f.liveViewers ?? 0), 0)
  const tone = markerTone(e, live)

  const chip = (text: string, bg: string, fg = '#fff') =>
    `<span style="display:inline-block;padding:1px 6px;border-radius:9999px;`
    + `background:${bg};color:${fg};font-size:10px;font-weight:600;letter-spacing:.02em">`
    + `${escapeHtml(text)}</span>`

  const rows: string[] = []
  rows.push(
    `<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-bottom:4px">`
    + (live ? chip('ON AIR', '#dc2626') : chip(eventStatusLabel(e.statusCode), tone.fill))
    + (isSensitive(e.classificationCode)
      ? chip(classificationLabel(e.classificationCode), '#7c3aed') : '')
    + `</div>`,
  )
  rows.push(`<strong style="font-size:13px">${escapeHtml(e.title)}</strong>`)
  rows.push(`<div style="opacity:.75">${escapeHtml(eventTypeLabel(e.eventTypeCode))} · `
    + `${escapeHtml(placeOf(e))}</div>`)

  if (feeds.length > 0) {
    rows.push(`<div style="margin-top:4px">`
      + `<b>${feeds.length}</b> camera${feeds.length === 1 ? '' : 's'}`
      + (onAir > 0 ? ` · <b style="color:#dc2626">${onAir} on air</b>` : '')
      + (viewers > 0 ? ` · ${viewers} watching` : '')
      + `</div>`)
    const recording = feeds.filter((f) => f.recordingStateCode === 'RECORDING'
      || f.recordingStateCode === 'PENDING').length
    const ready = feeds.filter((f) => f.recordingStateCode === 'READY').length
    if (recording > 0 || ready > 0) {
      rows.push(`<div style="opacity:.75">`
        + (recording > 0 ? `${recording} recording` : '')
        + (recording > 0 && ready > 0 ? ' · ' : '')
        + (ready > 0 ? `${ready} in the archive` : '')
        + `</div>`)
    }
  } else {
    rows.push(`<div style="opacity:.75;margin-top:4px">No cameras on this event</div>`)
  }

  // Provenance, always.
  if (e.origin === 'device') {
    const acc = e.accuracyM != null
      ? ` ±${e.accuracyM < 1000 ? `${e.accuracyM} m` : `${(e.accuracyM / 1000).toFixed(1)} km`}`
      : ''
    rows.push(`<div style="margin-top:5px;padding-top:5px;border-top:1px solid rgba(128,128,128,.25);opacity:.8">`
      + `📍 Camera position${escapeHtml(acc)}`
      + (e.fixFrom ? ` · ${escapeHtml(e.fixFrom)}` : '')
      + `</div>`)
  } else {
    rows.push(`<div style="margin-top:5px;padding-top:5px;border-top:1px solid rgba(128,128,128,.25);opacity:.8">`
      + `◌ Planned position — no camera has reported one`
      + `</div>`)
  }

  return `<div style="font-size:12px;line-height:1.4">${rows.join('')}</div>`
}

/**
 * Popup content is set as HTML because that is Mapbox's API, so every
 * interpolated value is escaped here. An event title is user-supplied text.
 */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c
  ))
}
